import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import FileUploader from "@/components/FileUploader";
import { REGIONS } from "@/lib/countries";

const CATEGORIES = ["Food & beverage", "Coffee", "Tea", "Spices", "Textiles", "Handicrafts", "Furniture", "Cosmetics", "Agriculture", "Seafood", "Other"];

export default function ProductForm() {
  const { pid } = useParams();
  const nav = useNavigate();
  const editing = Boolean(pid);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({
    name: "", category: "Food & beverage", description: "", origin_country: "",
    hs_code: "", unit: "kg", moq: 1, price_min: "", price_max: "", currency: "EUR",
    lead_time_days: "", certifications: [], image_ids: [], image_urls: [], document_ids: [],
    sample_available: false, sample_price: "", sample_quantity: "", sample_unit: "",
  });

  useEffect(() => {
    if (editing) api.get(`/products/${pid}`).then((r) => {
      const p = r.data;
      setF({
        name: p.name || "", category: p.category || "Other", description: p.description || "",
        origin_country: p.origin_country || "", hs_code: p.hs_code || "",
        unit: p.unit || "kg", moq: p.moq || 1,
        price_min: p.price_min ?? "", price_max: p.price_max ?? "", currency: p.currency || "EUR",
        lead_time_days: p.lead_time_days ?? "", certifications: p.certifications || [],
        image_ids: p.image_ids || [], image_urls: p.image_urls || [], document_ids: p.document_ids || [],
        sample_available: !!p.sample_available,
        sample_price: p.sample_price ?? "", sample_quantity: p.sample_quantity ?? "",
        sample_unit: p.sample_unit || p.unit || "",
      });
    }).catch(() => toast.error("Failed to load product"));
  }, [pid, editing]);

  const setCert = (v, checked) => {
    setF((prev) => ({ ...prev, certifications: checked ? [...prev.certifications, v] : prev.certifications.filter((c) => c !== v) }));
  };

  const save = async (submit = false) => {
    setBusy(true);
    try {
      const payload = {
        ...f,
        moq: parseInt(f.moq) || 1,
        price_min: f.price_min === "" ? null : parseFloat(f.price_min),
        price_max: f.price_max === "" ? null : parseFloat(f.price_max),
        lead_time_days: f.lead_time_days === "" ? null : parseInt(f.lead_time_days),
        sample_price: f.sample_price === "" ? null : parseFloat(f.sample_price),
        sample_quantity: f.sample_quantity === "" ? null : parseFloat(f.sample_quantity),
        sample_unit: f.sample_unit || null,
      };
      let saved;
      if (editing) {
        const r = await api.patch(`/supplier/products/${pid}`, payload);
        saved = r.data;
      } else {
        const r = await api.post("/supplier/products", payload);
        saved = r.data;
      }
      if (submit) {
        await api.post(`/supplier/products/${saved.id}/submit`);
        toast.success("Product submitted for review");
      } else {
        toast.success(editing ? "Product updated" : "Product created");
      }
      nav(`/supplier/products/${saved.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const CERTS = ["HACCP", "ISO 22000", "CE marking", "Organic EU", "FDA-aligned", "Halal", "Fair Trade"];

  return (
    <DashboardLayout title={editing ? "Edit product" : "New product"}>
      <div className="max-w-4xl">
        <Card className="card-lift">
          <CardHeader><CardTitle className="font-heading tracking-tight">Product details</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="product-form">
              <div className="md:col-span-2"><Label>Product name</Label><Input data-testid="prd-name" value={f.name} onChange={(e)=>setF({...f,name:e.target.value})} /></div>
              <div>
                <Label>Category</Label>
                <select data-testid="prd-category" className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm" value={f.category} onChange={(e)=>setF({...f,category:e.target.value})}>
                  {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <Label>Origin country</Label>
                <select data-testid="prd-origin" className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm" value={f.origin_country} onChange={(e)=>setF({...f,origin_country:e.target.value})}>
                  <option value="">— Select country —</option>
                  {Object.entries(REGIONS).map(([region, countries]) => (
                    <optgroup key={region} label={region}>
                      {countries.map((c) => <option key={c} value={c}>{c}</option>)}
                    </optgroup>
                  ))}
                </select>
              </div>
              <div><Label>HS code</Label><Input data-testid="prd-hs" value={f.hs_code} onChange={(e)=>setF({...f,hs_code:e.target.value})} /></div>
              <div><Label>Unit (kg, pcs, ton…)</Label><Input data-testid="prd-unit" value={f.unit} onChange={(e)=>setF({...f,unit:e.target.value})} /></div>
              <div><Label>Minimum order (MOQ)</Label><Input data-testid="prd-moq" type="number" min="1" value={f.moq} onChange={(e)=>setF({...f,moq:e.target.value})} /></div>
              <div><Label>Lead time (days)</Label><Input data-testid="prd-lead" type="number" value={f.lead_time_days} onChange={(e)=>setF({...f,lead_time_days:e.target.value})} /></div>
              <div><Label>Price min (per {f.unit})</Label><Input data-testid="prd-pmin" type="number" step="0.01" value={f.price_min} onChange={(e)=>setF({...f,price_min:e.target.value})} /></div>
              <div><Label>Price max (per {f.unit})</Label><Input data-testid="prd-pmax" type="number" step="0.01" value={f.price_max} onChange={(e)=>setF({...f,price_max:e.target.value})} /></div>
              <div>
                <Label>Currency</Label>
                <select data-testid="prd-currency" className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm" value={f.currency} onChange={(e)=>setF({...f,currency:e.target.value})}>
                  <option>EUR</option><option>USD</option><option>VND</option>
                </select>
              </div>
              <div className="md:col-span-2"><Label>Description</Label><Textarea data-testid="prd-desc" rows={5} value={f.description} onChange={(e)=>setF({...f,description:e.target.value})} /></div>
              <div className="md:col-span-2">
                <Label>Certifications</Label>
                <div className="mt-2 flex flex-wrap gap-2" data-testid="prd-certs">
                  {CERTS.map((c) => (
                    <label key={c} className={`px-3 py-1.5 border rounded-md text-xs font-medium cursor-pointer ${f.certifications.includes(c) ? "bg-primary text-primary-foreground border-primary" : "bg-white border-border text-secondary"}`}>
                      <input type="checkbox" className="hidden" checked={f.certifications.includes(c)} onChange={(e)=>setCert(c, e.target.checked)} data-testid={`cert-${c.replace(/\s+/g,'-')}`} />
                      {c}
                    </label>
                  ))}
                </div>
              </div>
              <div className="md:col-span-2">
                <Label>Product images</Label>
                <FileUploader testid="images-uploader" value={f.image_ids} accept="image/*" purpose="product-image" label="Upload images" onChange={(ids) => setF({ ...f, image_ids: ids })} />
                <div className="mt-3 rounded-md border border-dashed border-border p-3 space-y-2">
                  <div className="text-xs font-heading font-black uppercase tracking-widest text-muted-foreground">
                    Or paste image URLs (one per line)
                  </div>
                  <textarea
                    data-testid="prd-image-urls"
                    rows={2}
                    value={(f.image_urls || []).join("\n")}
                    onChange={(e) => setF({ ...f, image_urls: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })}
                    placeholder="https://celacoffee.fi/api/product-images/...jpg"
                    className="w-full text-xs px-3 py-2 rounded border border-input bg-background font-mono"
                  />
                  {(f.image_urls || []).length > 0 && (
                    <div className="grid grid-cols-4 gap-2 mt-2" data-testid="image-urls-preview">
                      {f.image_urls.slice(0, 8).map((url, i) => (
                        <div key={i} className="aspect-square rounded overflow-hidden bg-muted border border-border relative">
                          <img src={url} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.opacity = "0.3"; }} />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="md:col-span-2">
                <Label>Compliance documents (PDF, certificates)</Label>
                <FileUploader testid="docs-uploader" value={f.document_ids} accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg" purpose="compliance-doc" label="Upload documents" onChange={(ids) => setF({ ...f, document_ids: ids })} />
              </div>
              <div className="md:col-span-2 border-t border-border pt-6 mt-2">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" data-testid="prd-sample-toggle" checked={f.sample_available} onChange={(e) => setF({ ...f, sample_available: e.target.checked })} className="w-4 h-4" />
                  <div>
                    <div className="font-semibold text-secondary">Offer paid sample</div>
                    <div className="text-xs text-muted-foreground">Let buyers order a small quantity to test quality before committing to the full MOQ.</div>
                  </div>
                </label>
              </div>
              {f.sample_available && (
                <>
                  <div><Label>Sample price (USD)</Label><Input data-testid="prd-sample-price" type="number" step="0.01" min="1" value={f.sample_price} onChange={(e) => setF({ ...f, sample_price: e.target.value })} /></div>
                  <div><Label>Sample quantity</Label><Input data-testid="prd-sample-qty" type="number" step="0.1" min="0.1" value={f.sample_quantity} onChange={(e) => setF({ ...f, sample_quantity: e.target.value })} /></div>
                  <div><Label>Sample unit</Label><Input data-testid="prd-sample-unit" value={f.sample_unit} onChange={(e) => setF({ ...f, sample_unit: e.target.value })} placeholder={f.unit} /></div>
                </>
              )}
            </div>
            <div className="mt-8 flex flex-wrap gap-3 justify-end">
              <Button data-testid="save-draft" variant="outline" onClick={() => save(false)} disabled={busy || !f.name}>{busy ? "Saving…" : (editing ? "Save changes" : "Save as draft")}</Button>
              <Button data-testid="submit-review" onClick={() => save(true)} disabled={busy || !f.name}>Submit for review</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
